import { useId, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Loader2, X } from "lucide-react";
import { useCreateLocation, useUpdateLocation } from "@/api/hooks";
import { ApiClientError } from "@/api/client";
import type { Address, Contact, Location, LocationType } from "@/api/types";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  LOCATION_CODE_MAX_LENGTH,
  LOCATION_TYPE_DESCRIPTIONS,
  LOCATION_TYPE_LABELS,
  locationCodeFromName,
  parentCandidates,
  validateLocation,
  type LocationErrors,
} from "../../locationForm";
import { SwitchField, TextField } from "../loan-rules/fields";

const NO_PARENT = "";

/** Drops blank entries so cleared fields are not stored as empty strings. */
function compact(values: Address | Contact): Record<string, string> {
  const result: Record<string, string> = {};
  for (const [key, value] of Object.entries(values as Record<string, string | undefined>)) {
    const trimmed = value?.trim();
    if (trimmed) result[key] = trimmed;
  }
  return result;
}

/**
 * Create a location (établissement), or edit an existing one. Mounted only
 * while open, so each opening starts from the location being edited.
 */
export function LocationDialog({
  locations,
  location,
  onClose,
  onSaved,
}: {
  locations: Location[];
  /** null creates a new location */
  location: Location | null;
  onClose: () => void;
  onSaved: (location: Location) => void;
}) {
  const isNew = location === null;
  const formId = useId();

  const [name, setName] = useState(location?.name ?? "");
  const [code, setCode] = useState(location?.code ?? "");
  // A new code follows the name until the administrator types one
  const [codeEdited, setCodeEdited] = useState(!isNew);
  const [type, setType] = useState<LocationType>(location?.type ?? "library");
  const [parentId, setParentId] = useState(location?.parentId ?? NO_PARENT);
  const [address, setAddress] = useState<Address>(location?.address ?? {});
  const [contact, setContact] = useState<Contact>(location?.contact ?? {});
  const [active, setActive] = useState(location?.active ?? true);
  const [errors, setErrors] = useState<LocationErrors>({});
  const [serverError, setServerError] = useState<string | null>(null);

  const createLocation = useCreateLocation();
  const updateLocation = useUpdateLocation();
  const isPending = createLocation.isPending || updateLocation.isPending;

  const parents = parentCandidates(locations, location?.id ?? null);
  // The API cannot detach a location from its parent, so only roots may stay roots
  const canBeRoot = isNew || location.parentId === null;

  const changeName = (value: string) => {
    setName(value);
    if (!codeEdited) setCode(locationCodeFromName(value));
    setErrors((current) => ({ ...current, name: undefined }));
  };

  const submit = async () => {
    setServerError(null);
    const nextErrors = validateLocation({ name, code }, locations, location?.id ?? null);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    const input = {
      name: name.trim(),
      code: code.trim(),
      type,
      ...(parentId !== NO_PARENT && { parentId }),
      address: compact(address),
      contact: compact(contact),
      active,
    };

    try {
      const saved = isNew
        ? await createLocation.mutateAsync(input)
        : await updateLocation.mutateAsync({ id: location.id, ...input });
      onSaved(saved);
    } catch (err) {
      if (err instanceof ApiClientError && err.status === 409) {
        setErrors({ code: "Ce code est déjà utilisé par un autre établissement." });
        return;
      }
      if (err instanceof ApiClientError && err.status === 403) {
        setServerError("Vous n'avez pas les droits pour modifier cet établissement.");
        return;
      }
      const violations = err instanceof ApiClientError ? (err.data?.violations ?? []) : [];
      const fieldErrors: LocationErrors = {};
      for (const violation of violations) {
        if (violation.propertyPath === "name" || violation.propertyPath === "code") {
          fieldErrors[violation.propertyPath] = violation.message;
        }
      }
      if (Object.keys(fieldErrors).length > 0) setErrors(fieldErrors);
      else setServerError(err instanceof Error ? err.message : "L'enregistrement a échoué");
    }
  };

  return (
    <Dialog.Root
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="animate-fade-in fixed inset-0 z-40 bg-gray-950/40 backdrop-blur-sm" />
        <Dialog.Content className="animate-fade-in fixed inset-y-0 right-0 z-50 flex w-full max-w-2xl flex-col bg-white shadow-2xl dark:bg-gray-900">
          <header className="flex items-start justify-between gap-4 border-b border-gray-200 px-6 py-5 dark:border-gray-700">
            <div>
              <Dialog.Title className="text-lg font-semibold text-gray-900 dark:text-white">
                {isNew ? "Nouvel établissement" : `Modifier « ${location.name} »`}
              </Dialog.Title>
              <Dialog.Description className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {isNew
                  ? "Il pourra recevoir des exemplaires et des comptes dès sa création."
                  : "Les exemplaires et les comptes rattachés restent en place."}
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <Button variant="ghost" size="icon" aria-label="Fermer">
                <X aria-hidden />
              </Button>
            </Dialog.Close>
          </header>

          <form
            id={formId}
            noValidate
            onSubmit={(event) => {
              event.preventDefault();
              void submit();
            }}
            className="min-h-0 flex-1 space-y-8 overflow-y-auto px-6 py-6"
          >
            <section aria-labelledby={`${formId}-identity`} className="space-y-5">
              <SectionTitle id={`${formId}-identity`}>Identité</SectionTitle>

              <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_12rem]">
                <TextField
                  label="Nom"
                  value={name}
                  onChange={changeName}
                  error={errors.name}
                  placeholder="ex. Médiathèque Jules-Verne"
                  maxLength={255}
                  autoFocus
                />
                <TextField
                  label="Code"
                  value={code}
                  onChange={(value) => {
                    setCode(value.toLowerCase());
                    setCodeEdited(true);
                    setErrors((current) => ({ ...current, code: undefined }));
                  }}
                  error={errors.code}
                  hint={isNew && !codeEdited ? "Proposé d'après le nom" : "Identifiant unique"}
                  maxLength={LOCATION_CODE_MAX_LENGTH}
                  mono
                />
              </div>

              <fieldset>
                <legend className="mb-1.5 text-sm text-gray-700 dark:text-gray-200">Type</legend>
                <div className="grid gap-3 sm:grid-cols-2">
                  {(Object.keys(LOCATION_TYPE_LABELS) as LocationType[]).map((option) => (
                    <label
                      key={option}
                      className={cn(
                        "has-focus-visible:ring-ring cursor-pointer rounded-xl border p-4 transition-colors has-focus-visible:ring-2",
                        type === option
                          ? "border-primary/50 bg-primary/5 dark:bg-primary/10"
                          : "border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600"
                      )}
                    >
                      <span className="flex items-center gap-2.5">
                        <input
                          type="radio"
                          name={`${formId}-type`}
                          value={option}
                          checked={type === option}
                          onChange={() => {
                            setType(option);
                          }}
                          className="accent-primary h-4 w-4"
                        />
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {LOCATION_TYPE_LABELS[option]}
                        </span>
                      </span>
                      <span className="mt-1 block pl-6.5 text-xs text-gray-500 dark:text-gray-400">
                        {LOCATION_TYPE_DESCRIPTIONS[option]}
                      </span>
                    </label>
                  ))}
                </div>
              </fieldset>

              <div>
                <label
                  htmlFor={`${formId}-parent`}
                  className="mb-1.5 block text-sm text-gray-700 dark:text-gray-200"
                >
                  Rattaché à
                </label>
                <select
                  id={`${formId}-parent`}
                  value={parentId}
                  onChange={(event) => {
                    setParentId(event.target.value);
                  }}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                >
                  {canBeRoot && <option value={NO_PARENT}>Aucun (niveau le plus haut)</option>}
                  {parents.map((candidate) => (
                    <option key={candidate.id} value={candidate.id}>
                      {"  ".repeat(candidate.depth)}
                      {candidate.name} · {LOCATION_TYPE_LABELS[candidate.type]}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Les comptes d'un établissement parent ont accès à celui-ci.
                </p>
              </div>
            </section>

            <section aria-labelledby={`${formId}-address`} className="space-y-4">
              <SectionTitle id={`${formId}-address`}>Adresse et contact</SectionTitle>
              <TextField
                label="Adresse"
                value={address.street ?? ""}
                onChange={(street) => {
                  setAddress((current) => ({ ...current, street }));
                }}
                maxLength={255}
                autoComplete="street-address"
              />
              <div className="grid gap-4 sm:grid-cols-[9rem_minmax(0,1fr)]">
                <TextField
                  label="Code postal"
                  value={address.postalCode ?? ""}
                  onChange={(postalCode) => {
                    setAddress((current) => ({ ...current, postalCode }));
                  }}
                  maxLength={10}
                  autoComplete="postal-code"
                />
                <TextField
                  label="Ville"
                  value={address.city ?? ""}
                  onChange={(city) => {
                    setAddress((current) => ({ ...current, city }));
                  }}
                  maxLength={100}
                  autoComplete="address-level2"
                />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <TextField
                  label="E-mail"
                  type="email"
                  value={contact.email ?? ""}
                  onChange={(email) => {
                    setContact((current) => ({ ...current, email }));
                  }}
                  maxLength={255}
                  autoComplete="email"
                />
                <TextField
                  label="Téléphone"
                  type="tel"
                  value={contact.phone ?? ""}
                  onChange={(phone) => {
                    setContact((current) => ({ ...current, phone }));
                  }}
                  maxLength={30}
                  autoComplete="tel"
                />
              </div>
            </section>

            <SwitchField label="Établissement actif" checked={active} onChange={setActive} />
          </form>

          <footer className="flex flex-wrap items-center justify-end gap-3 border-t border-gray-200 px-6 py-4 dark:border-gray-700">
            {serverError && (
              <p role="alert" className="mr-auto text-sm text-red-700 dark:text-red-400">
                {serverError}
              </p>
            )}
            <Dialog.Close asChild>
              <Button variant="ghost">Annuler</Button>
            </Dialog.Close>
            <Button type="submit" form={formId} disabled={isPending}>
              {isPending && <Loader2 className="animate-spin" aria-hidden />}
              {isNew ? "Créer l'établissement" : "Enregistrer"}
            </Button>
          </footer>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function SectionTitle({ id, children }: { id: string; children: string }) {
  return (
    <h3 id={id} className="text-sm font-semibold text-gray-900 dark:text-white">
      {children}
    </h3>
  );
}
